import { customElement, resolve } from 'aurelia';
import { IRouter } from '@aurelia/router';
import { I18N } from '@aurelia/i18n';
import template from './persons-list.html';
import {
  deletePerson,
  deletePersonImportMapping,
  deletePersonImportMappings,
  formatPersonName,
  listPersonImportMappings,
  listPersons,
  mergePersons,
} from '../services/api.service';
import type { Person, PersonImportMapping } from '../services/api.types';
import { ConfirmDialog } from '../components/confirm-dialog';

@customElement({ name: 'persons-list', template })
export class PersonsList {
  private readonly i18n: I18N = resolve(I18N);
  private readonly router: IRouter = resolve(IRouter);

  public persons: Person[] = [];
  public personMappings: PersonImportMapping[] = [];
  public errorMessage = '';
  public sortBy: 'usageCount' | 'name' = 'usageCount';
  public activeTab: 'persons' | 'mappings' = 'persons';
  public personsLoading = false;
  public mappingLoading = false;
  public selectedMappingIds: Record<string, boolean> = {};
  public visiblePersonStart = 0;
  public visiblePersonEnd = 0;

  public personScrollContainer: HTMLElement | null = null;
  public personTableEl: HTMLTableElement | null = null;
  private _visibleRangeResizeObserver: ResizeObserver | null = null;
  private _visibleRangeFrame: number | null = null;
  private readonly _visibleRangeScrollHandler = (): void => this.scheduleVisiblePersonRangeUpdate();
  private readonly _visibleRangeResizeHandler = (): void => this.scheduleVisiblePersonRangeUpdate();

  public showPersonModal = false;
  public personToEdit: Person | null = null;

  public showMergePicker = false;
  public personToMerge: Person | null = null;

  public formatPersonName = formatPersonName;

  public async loading(): Promise<void> {
    await this.fetchAll();
  }

  public attached(): void {
    this.personScrollContainer?.addEventListener('scroll', this._visibleRangeScrollHandler, { passive: true });
    window.addEventListener('resize', this._visibleRangeResizeHandler);
    if (this.personScrollContainer) {
      this._visibleRangeResizeObserver = new ResizeObserver(() => this.scheduleVisiblePersonRangeUpdate());
      this._visibleRangeResizeObserver.observe(this.personScrollContainer);
    }
    this.scheduleVisiblePersonRangeUpdate();
  }

  public detaching(): void {
    this.personScrollContainer?.removeEventListener('scroll', this._visibleRangeScrollHandler);
    window.removeEventListener('resize', this._visibleRangeResizeHandler);
    this._visibleRangeResizeObserver?.disconnect();
    this._visibleRangeResizeObserver = null;
    if (this._visibleRangeFrame !== null) {
      cancelAnimationFrame(this._visibleRangeFrame);
      this._visibleRangeFrame = null;
    }
  }

  public get selectedMappingCount(): number {
    return Object.values(this.selectedMappingIds).filter(Boolean).length;
  }

  public get allMappingsSelected(): boolean {
    return this.personMappings.length > 0
      && this.personMappings.every(mapping => this.selectedMappingIds[mapping.id]);
  }

  public set allMappingsSelected(value: boolean) {
    const next: Record<string, boolean> = {};
    if (value) {
      for (const mapping of this.personMappings)
        next[mapping.id] = true;
    }
    this.selectedMappingIds = next;
  }

  public get someMappingsSelected(): boolean {
    return this.selectedMappingCount > 0 && !this.allMappingsSelected;
  }

  public async fetchAll(): Promise<void> {
    this.personsLoading = true;
    try {
      this.persons = await listPersons(this.sortBy, this.sortBy === 'usageCount');
    } catch (e: unknown) {
      this.errorMessage = e instanceof Error ? e.message : String(e);
    } finally {
      this.personsLoading = false;
      this.scheduleVisiblePersonRangeUpdate();
    }
  }

  public async setTab(tab: 'persons' | 'mappings'): Promise<void> {
    if (this.activeTab === tab) return;
    this.activeTab = tab;
    if (tab === 'mappings') {
      await this.fetchMappings();
    }
    this.scheduleVisiblePersonRangeUpdate();
  }

  public async fetchMappings(): Promise<void> {
    this.mappingLoading = true;
    try {
      this.personMappings = await listPersonImportMappings();
      const availableIds = new Set(this.personMappings.map(mapping => mapping.id));
      this.selectedMappingIds = Object.fromEntries(
        Object.entries(this.selectedMappingIds).filter(([id, selected]) => selected && availableIds.has(id)),
      );
    } catch (e: unknown) {
      this.errorMessage = e instanceof Error ? e.message : String(e);
    } finally {
      this.mappingLoading = false;
    }
  }

  public onMappingSelectionChanged(id: string, event: Event): void {
    const target = event.target;
    if (!(target instanceof HTMLInputElement)) return;
    const next = { ...this.selectedMappingIds };
    if (target.checked) next[id] = true;
    else delete next[id];
    this.selectedMappingIds = next;
  }

  public mappingDisplayName(mapping: PersonImportMapping): string {
    const sourceName = [mapping.sourceFirstName, mapping.sourceLastName].filter(Boolean).join(' ');
    if (sourceName) return mapping.sourceNickName ? `${sourceName} (${mapping.sourceNickName})` : sourceName;
    const targetName = [mapping.targetFirstName, mapping.targetLastName].filter(Boolean).join(' ');
    if (targetName) return mapping.targetNickName ? `${targetName} (${mapping.targetNickName})` : targetName;
    return mapping.targetPersonId;
  }

  public async removeMapping(mapping: PersonImportMapping): Promise<void> {
    const confirmed = await ConfirmDialog.confirm(
      this.i18n.tr('persons.mappings.deleteConfirm', { name: this.mappingDisplayName(mapping) }),
      this.i18n.tr('persons.mappings.deleteTitle'),
    );
    if (!confirmed) return;

    try {
      await deletePersonImportMapping(mapping.id);
      this.personMappings = this.personMappings.filter(candidate => candidate.id !== mapping.id);
      const next = { ...this.selectedMappingIds };
      delete next[mapping.id];
      this.selectedMappingIds = next;
    } catch (e: unknown) {
      this.errorMessage = e instanceof Error ? e.message : String(e);
    }
  }

  public async removeSelectedMappings(): Promise<void> {
    const ids = Object.keys(this.selectedMappingIds).filter(id => this.selectedMappingIds[id]);
    if (ids.length === 0) return;

    const confirmed = await ConfirmDialog.confirm(
      this.i18n.tr('persons.mappings.deleteSelectedConfirm', { count: ids.length }),
      this.i18n.tr('persons.mappings.deleteTitle'),
    );
    if (!confirmed) return;

    try {
      await deletePersonImportMappings(ids);
      const selected = new Set(ids);
      this.personMappings = this.personMappings.filter(mapping => !selected.has(mapping.id));
      this.selectedMappingIds = {};
    } catch (e: unknown) {
      this.errorMessage = e instanceof Error ? e.message : String(e);
    }
  }

  public async setSort(sortBy: 'usageCount' | 'name'): Promise<void> {
    if (this.sortBy === sortBy) return;
    this.sortBy = sortBy;
    await this.fetchAll();
  }

  public birthdayLabel(person: Person): string {
    if (person.birthYear == null && person.birthMonth == null && person.birthDay == null) return '—';
    const month = person.birthMonth != null ? String(person.birthMonth).padStart(2, '0') : '??';
    const day = person.birthDay != null ? String(person.birthDay).padStart(2, '0') : '??';
    const year = person.birthYear != null ? String(person.birthYear) : '????';
    return `${day}.${month}.${year}`;
  }

  public openCreateModal(): void {
    this.personToEdit = null;
    this.showPersonModal = true;
  }

  public openEditModal(person: Person): void {
    this.personToEdit = person;
    this.showPersonModal = true;
  }

  public async onPersonModalSaved(person: Person): Promise<void> {
    this.showPersonModal = false;
    this.personToEdit = null;
    // Refetch from the server so sort order (by usage count) and canonical
    // field values (e.g. server-computed usageCount) stay correct, rather than
    // relying on an optimistic local merge that could leave the new/edited
    // person invisible if it sorts outside the visible area unexpectedly.
    await this.fetchAll();
  }

  public onPersonModalClosed(): void {
    this.showPersonModal = false;
    this.personToEdit = null;
  }

  public async removePerson(person: Person): Promise<void> {
    const confirmed = await ConfirmDialog.confirm(
      this.i18n.tr('persons.delete.confirm', { name: `${person.firstName} ${person.lastName}` }),
      this.i18n.tr('persons.delete.confirmTitle'),
    );
    if (!confirmed) return;
    try {
      await deletePerson(person.id);
      this.persons = this.persons.filter(p => p.id !== person.id);
      this.scheduleVisiblePersonRangeUpdate();
    } catch (e: unknown) {
      this.errorMessage = e instanceof Error ? e.message : String(e);
    }
  }

  // ── Merge ─────────────────────────────────────────────────────────────────

  public openMergePicker(person: Person): void {
    this.personToMerge = person;
    this.showMergePicker = true;
  }

  public onMergePickerClosed(): void {
    this.showMergePicker = false;
    this.personToMerge = null;
  }

  public async onMergeTargetSelected(target: Person): Promise<void> {
    this.showMergePicker = false;
    const source = this.personToMerge;
    this.personToMerge = null;
    if (!source) return;

    if (target.id === source.id) {
      await ConfirmDialog.alert(this.i18n.tr('persons.merge.sameError'), this.i18n.tr('persons.merge.title'));
      return;
    }

    const confirmed = await ConfirmDialog.confirm(
      this.i18n.tr('persons.merge.confirm', { source: this.formatPersonName(source), target: this.formatPersonName(target) }),
      this.i18n.tr('persons.merge.title'),
    );
    if (!confirmed) return;

    try {
      await mergePersons(source.id, target.id);
      await this.fetchAll();
    } catch (e: unknown) {
      this.errorMessage = e instanceof Error ? e.message : String(e);
    }
  }

  // Jumps to the gallery pre-filtered to exactly this person — a full page navigation
  // (not the router) since gallery-view's loading() hook reads these from the query
  // string, mirroring the existing protocolNumber query-param pattern.
  public viewInGallery(person: Person): void {
    const params = new URLSearchParams({ personId: person.id });
    if (person.firstName) params.set('personFirstName', person.firstName);
    if (person.lastName) params.set('personLastName', person.lastName);
    if (person.nickName) params.set('personNickName', person.nickName);
    window.location.assign(`/gallery?${params.toString()}`);
  }

  public async rateFaceMarkers(person: Person): Promise<void> {
    await this.router.load(`persons/${person.id}/face-ratings`);
  }

  public async rateAllFaceMarkers(): Promise<void> {
    await this.router.load('persons/face-ratings');
  }

  private scheduleVisiblePersonRangeUpdate(): void {
    if (this._visibleRangeFrame !== null) return;
    this._visibleRangeFrame = requestAnimationFrame(() => {
      this._visibleRangeFrame = null;
      this.updateVisiblePersonRange();
    });
  }

  private updateVisiblePersonRange(): void {
    const container = this.personScrollContainer;
    const rows = Array.from(this.personTableEl?.querySelectorAll<HTMLTableRowElement>('tbody tr') ?? []);
    if (this.activeTab !== 'persons' || this.personsLoading || !container || rows.length === 0) {
      this.visiblePersonStart = 0;
      this.visiblePersonEnd = 0;
      return;
    }

    const containerRect = container.getBoundingClientRect();
    const viewportTop = Math.max(containerRect.top, 0);
    const viewportBottom = Math.min(containerRect.bottom, window.innerHeight);
    let firstVisible = -1;
    let lastVisible = -1;

    for (let index = 0; index < rows.length; index++) {
      const rect = rows[index].getBoundingClientRect();
      if (rect.bottom > viewportTop && rect.top < viewportBottom) {
        if (firstVisible < 0) firstVisible = index;
        lastVisible = index;
      }
    }

    if (firstVisible < 0) {
      this.visiblePersonStart = 0;
      this.visiblePersonEnd = 0;
      return;
    }

    this.visiblePersonStart = firstVisible + 1;
    this.visiblePersonEnd = lastVisible + 1;
  }
}
